import React, { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'
import { BasicButton } from '@pokemon/design.ui.input.basic-button'
import { getSupportLink } from '@tpci/i18next-ext'

/**
 * Props to use when initializing an `ErrorFallback` component.
 */
export interface ErrorFallbackProps {
  /**
   * Header to display on Error page. If unset, defaults to localized "Oops! It looks like something went wrong."
   */
  header?: string

  /**
   * Optional additional body text to display on Error page.
   */
  text?: string

  /**
   * Options for displaying a primary color button. By default, shows "Contact Customer Support" button.
   * 
   * Set to `false` to disable the button.
   */
  primaryButton?: ErrorButtonProps | false

  /**
   * Options for displaying a secondary color button. By default, shows "Return to Pokemon.com" button.
   * 
   * Set to `false` to disable the button.
   */
  secondaryButton?: ErrorButtonProps | false

  /**
   * In case you need a custom body for the error you can use this prop to display it
   */
  customBody?: ReactNode;

}

/**
 * Props defining the behavior and text for the buttons in the `ErrorFallback` UI.
 */
export interface ErrorButtonProps {
  /**
   * Text to display.
   */
  text?: string

  /**
   * URL to link to when a user clicks the button.
   */
  url?: string
}

/**
 * A fallback component displayed in case of errors.
 */
export function ErrorFallback({ header, text, primaryButton = {}, secondaryButton = {}, customBody }: ErrorFallbackProps) {
  const { t, i18n } = useTranslation()

  return (
    <>
      {
        customBody ||
        <div className='title-wrapper'>
          <h1>
            {header || t('generic_error', 'Oops! It looks like something went wrong.')}
          </h1>
          {text &&
            <p className='sub-title'>
              {text}
            </p>
          }
        </div>
      }
      {primaryButton ?
        <BasicButton
          type={'link'}
          href={primaryButton.url || getSupportLink(i18n.language)}
          text={primaryButton.text || t('customer_support', 'Contact Customer Support')} /> :
        null}
      {secondaryButton ?
        <BasicButton
          type={'link'}
          href={secondaryButton.url || "https://pokemon.com"}
          text={secondaryButton.text || t('return_button', 'Return to Pokemon.com')}
          style={{ marginTop: '10px', width: '100%' }} buttonStatus={'secondary'} /> :
        null}
    </>
  )
}
